import React from 'react';
import Header from '../components/Header';
import BrandLogos from '../components/BrandLogos';
import VideoSection from '../components/VideoSection';
import WhoShouldAttend from '../components/WhoShouldAttend';
import WhyShouldAttend from '../components/WhyShouldAttend';
import WhyZyluOnly from '../components/WhyZyluOnly';
// import SuccessStories from '../components/SuccessStories';
import MoneyBackGuarantee from '../components/MoneyBackGuarantee';
import CTAButton from '../components/CTAButton';
import PainPoints from '../components/PainPoints';
import Solution from '../components/Solution';
import Results from '../components/Results';
import Testimonials from '../components/Testimonials';
import BookingForm from '../components/BookingForm';
import StickyTimer from '../components/StickyTimer';
import { useModal } from '../contexts/ModalContext';
import { TrendingUp, IndianRupee, BarChart3, Clock } from 'lucide-react';
import { motion } from 'framer-motion';
import AnimatedText from '../components/AnimatedText';


const RevenueLandingPage: React.FC = () => {
  const { handleBookNowClick } = useModal();

  return (
    <div className="min-h-screen">
      <Header />
      
      {/* Hero Section */}
      <section className="relative bg-white text-black py-12 md:py-20 overflow-hidden">
        {/* Animated Background Elements */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-32 -right-32 w-72 h-72 bg-gradient-to-br from-red-200 to-red-300 rounded-full opacity-20 animate-float"></div>
          <div className="absolute -bottom-32 -left-32 w-80 h-80 bg-gradient-to-tr from-black/5 to-gray-200 rounded-full opacity-25 animate-bounce-subtle"></div>
          <div className="absolute top-20 right-20 w-48 h-48 bg-gradient-to-r from-red-100 to-red-200 rounded-full opacity-30 animate-pulse"></div>
          <div className="absolute bottom-20 left-20 w-56 h-56 bg-gradient-to-l from-gray-100 to-gray-200 rounded-full opacity-20 animate-float" style={{ animationDelay: '1s' }}></div>
        </div>
        
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="relative z-10 grid lg:grid-cols-2 gap-8 md:gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="order-1 lg:order-1"
            >
              <AnimatedText
                text="Let's fix your salon growth"
                className="text-3xl sm:text-4xl md:text-5xl lg:text-7xl font-black leading-tight text-black-600"
                type="fade"
              />
              <AnimatedText
                text="in 30 Minutes."
                className="text-3xl sm:text-4xl md:text-5xl lg:text-7xl font-black leading-tight text-red-600 mb-4 md:mb-6"
                type="slide"
                delay={0.3}
              />
              <AnimatedText
                text="Zylu is a salon management & growth automation software built for beauty & wellness centers."
                className="text-lg sm:text-xl md:text-2xl text-gray-600 mb-3 md:mb-4 font-medium"
                type="fade"
                delay={0.6}
              />
              <AnimatedText
                text="🎯 Break free today — starting at just ₹625/month!"
                className="text-lg sm:text-xl md:text-2xl lg:text-3xl text-red-600 mb-6 md:mb-8 font-bold"
                type="fade"
                delay={0.9}
              />
              
              <div className="flex flex-col md:flex-row gap-4 md:gap-6 mb-8 md:mb-12">
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.9 }}
                >
                  <CTAButton 
                    text="Book Now Free Demo"
                    onClick={handleBookNowClick}
                    className="text-lg md:text-xl py-4 md:py-6 px-6 md:px-8"
                  />
                </motion.div>
                <motion.div 
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 1.1 }}
                  className="text-center md:text-left"
                >
                  <p className="text-base md:text-lg font-semibold text-black mb-2">✓ 30-Minute Demo</p>
                  <p className="text-base md:text-lg font-semibold text-black mb-2">✓ Revenue Boost Guarantee</p>
                </motion.div>
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="grid grid-cols-2 gap-4 md:gap-6 order-2 lg:order-2 relative"
            >
              {/* Background Animation */}
                      <div className="absolute inset-0 bg-red-50 rounded-2xl animate-pulse opacity-30 -z-10"></div>
        <div className="absolute -inset-4 bg-red-200 rounded-3xl blur-xl opacity-20 animate-bounce-subtle -z-10"></div>
              
              <motion.div 
                whileHover={{ scale: 1.05 }}
                className="bg-red-600 p-4 md:p-6 rounded-xl text-center text-white shadow-lg"
              >
                <TrendingUp className="h-8 w-8 md:h-12 md:w-12 mx-auto mb-2 md:mb-4" />
                <div className="text-2xl md:text-4xl font-bold mb-1 md:mb-2">40%+</div>
                <p className="text-sm md:text-base font-semibold">Average Revenue Increase</p>
              </motion.div>
              <motion.div 
                whileHover={{ scale: 1.05 }}
                className="bg-white text-black p-4 md:p-6 rounded-xl text-center border-2 border-gray-200 shadow-lg"
              >
                <IndianRupee className="h-8 w-8 md:h-12 md:w-12 mx-auto mb-2 md:mb-4 text-red-600" />
                <div className="text-2xl md:text-4xl font-bold text-red-600 mb-1 md:mb-2">75K+</div>
                <p className="text-sm md:text-base font-semibold">Average Monthly Savings</p>
              </motion.div>
              <motion.div 
                whileHover={{ scale: 1.05 }}
                className="bg-gray-100 p-4 md:p-6 rounded-xl text-center text-black shadow-lg"
              >
                <BarChart3 className="h-8 w-8 md:h-12 md:w-12 mx-auto mb-2 md:mb-4 text-black" />
                <div className="text-2xl md:text-4xl font-bold text-black mb-1 md:mb-2">2,000+</div>
                <p className="text-sm md:text-base font-semibold">Successful Salons</p>
              </motion.div>
              <motion.div 
                whileHover={{ scale: 1.05 }}
                className="bg-red-600 p-4 md:p-6 rounded-xl text-center text-white shadow-lg"
              >
                <Clock className="h-8 w-8 md:h-12 md:w-12 mx-auto mb-2 md:mb-4 text-white-600" />
                <div className="text-2xl md:text-4xl font-bold mb-1 md:mb-2">24/7</div>
                <p className="text-sm md:text-base font-semibold">Automated Bookings</p>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      <BrandLogos />
      <VideoSection 
        videoId="55f7eddc245447d080727917348500de"
        videoType="loom"
        title="See How to Boost Your Revenue by 28%+"
        description="Watch this exclusive demo showing exactly how Zylu helps salon owners boost their revenue"
      />
      <WhoShouldAttend />
      <WhyShouldAttend />
      <WhyZyluOnly />
      <PainPoints />
      <Solution />
      <Results />
      {/* <SuccessStories /> */}
      <Testimonials />
      <MoneyBackGuarantee />
      <StickyTimer page="revenue" />
      
    </div>
  );
};

export default RevenueLandingPage;